import React, { useState, useEffect, useCallback, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { getComplianceChecks, getVersions, addVersion, getParameterComparison, getParameterHistory } from '../../shared/utils/comptabiliteService';
import { fetchCurrentFiscalYearContext, resolveFiscalYearLabel } from './utils/fiscalYearUtils';
import { logger } from '../../shared/utils/logger';
import { generateReportPDF } from '../../shared/utils/reportPdfGenerator';
import CustomSelect from '../../shared/components/custom/CustomSelect';
import { JournalElectroniqueViewer } from './EcrituresComptablesPage';
import {
  FileText,
  Shield,
  Download,
  CheckCircle,
  AlertTriangle,
  Calendar,
  X,
  Users,
  HardDrive,
  Database,
  Package,
  Clock,
  User,
  Plus,
  Tag,
  Filter,
  RefreshCw,
  AlertCircle,
  Settings,
  ArrowRightLeft,
  ChevronDown,
  ChevronUp,
  Search,
  List,
  Eye,
  BarChart3,
} from 'lucide-react';

const ComplianceModalShell = ({ open, onClose, title, Icon, iconClass, children, footer, maxWidth = '960px' }) => {
  if (!open) return null;
  return ReactDOM.createPortal(
    <div className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4" onClick={onClose}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-gradient-to-br from-slate-800 via-blue-750 to-slate-800 backdrop-blur-lg border-2 border-blue-400/60 rounded-2xl max-h-[90vh] flex flex-col overflow-hidden"
        style={{ boxShadow: '0 0 40px rgba(59, 130, 246, 0.4), 0 0 80px rgba(59, 130, 246, 0.2)', width: '95%', maxWidth }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-6 border-b border-white/10 shrink-0">
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            {Icon && <Icon className={`h-6 w-6 ${iconClass}`} />}
            {title}
          </h2>
          <button type="button" onClick={onClose} className="text-white/70 hover:text-white transition-colors">
            <X size={24} />
          </button>
        </div>
        <div className="overflow-y-auto p-6 flex-1">{children}</div>
        {footer && <div className="p-4 border-t border-white/10 flex justify-end gap-2 flex-wrap shrink-0">{footer}</div>}
      </motion.div>
    </div>,
    document.body
  );
};

const CollapsibleSection = ({ icon: Icon, iconClass, title, expanded, onToggle, children, headerExtra }) => (
  <div className="bg-white/5 rounded-xl border border-white/20 overflow-hidden">
    <button type="button" onClick={onToggle} className="w-full flex items-center justify-between p-4 hover:bg-white/5 transition-colors gap-3">
      <div className="flex items-center gap-3 min-w-0">
        <Icon size={20} className={iconClass} />
        <h3 className="text-lg font-semibold text-white truncate">{title}</h3>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {headerExtra}
        {expanded ? <ChevronUp size={20} className="text-white/60" /> : <ChevronDown size={20} className="text-white/60" />}
      </div>
    </button>
    <AnimatePresence>
      {expanded && (
        <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3 }} className="overflow-hidden">
          <div className="p-4 pt-0">{children}</div>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

const VersionLogViewer = ({ isOpen, onClose }) => {
  const { t, i18n } = useTranslation();
  const p = (key, opts) => t(`modules.comptabilite.pages.conformiteAuditPage.${key}`, opts);
  const [versions, setVersions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [versionLogTab, setVersionLogTab] = useState('overview');
  const versionLogTabs = useMemo(() => {
    const tabs = [
      { id: 'overview', label: p('current_version'), icon: CheckCircle },
      { id: 'history', label: p('version_history_title'), icon: Clock },
    ];
    if (showAddForm) {
      tabs.splice(1, 0, { id: 'add', label: p('add_new_version'), icon: Plus });
    }
    return tabs;
  }, [p, showAddForm]);
  const [newVersion, setNewVersion] = useState({
    version_number: '',
    release_date: new Date().toISOString().split('T')[0],
    description: '',
    changelog: '',
    installed_by: '',
    installation_date: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    if (isOpen) {
      loadVersions();
      setVersionLogTab('overview');
    }
  }, [isOpen]);

  const loadVersions = async () => {
    setIsLoading(true);
    try {
      const data = await getVersions();
      setVersions(data);
    } catch (error) {
      logger.error('Error loading versions:', error);
    }
    setIsLoading(false);
  };

  const handleAddVersion = async () => {
    if (!newVersion.version_number) {
      alert(p('alert_version_number_required'));
      return;
    }

    try {
      await addVersion(newVersion);
      await loadVersions();
      setShowAddForm(false);
      setNewVersion({
        version_number: '',
        release_date: new Date().toISOString().split('T')[0],
        description: '',
        changelog: '',
        installed_by: '',
        installation_date: new Date().toISOString().split('T')[0]
      });
    } catch (error) {
      logger.error('Error adding version:', error);
      alert(p('alert_add_version_error'));
    }
  };

  const getCurrentVersion = () => {
    if (versions.length === 0) return null;
    return versions.reduce((latest, v) => {
      const latestDate = new Date(latest.installation_date || latest.release_date);
      const vDate = new Date(v.installation_date || v.release_date);
      return vDate > latestDate ? v : latest;
    });
  };

  const currentVersion = getCurrentVersion();

  if (!isOpen) return null;

  return (
    <ComplianceModalShell
      open={isOpen}
      onClose={onClose}
      title={p('version_history_title')}
      Icon={Package}
      iconClass="text-purple-400"
      maxWidth="960px"
      footer={(
        <>
          <button type="button" onClick={onClose} className="px-4 py-2.5 rounded-xl bg-white/10 border border-white/20 text-white hover:bg-white/15 transition-all font-medium">
            {p('close')}
          </button>
          <button
            type="button"
            onClick={() => { setVersionLogTab('add'); setShowAddForm(true); }}
            className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-purple-500 to-pink-600 text-white font-semibold shadow-lg shadow-purple-500/30 flex items-center gap-2"
          >
            <Plus size={16} />
            {p('new_version')}
          </button>
        </>
      )}
    >
      <div className="space-y-4">
        <p className="text-white/60 text-sm">{p('version_tracking_subtitle')}</p>

        <div className="shrink-0 border-b border-white/10 pb-0">
          <div className="flex flex-wrap gap-1" role="tablist">
            {versionLogTabs.map((tab) => {
              const TabIcon = tab.icon;
              const active = versionLogTab === tab.id;
              return (
                <button key={tab.id} type="button" role="tab" aria-selected={active} onClick={() => setVersionLogTab(tab.id)} className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-t-xl text-sm font-semibold transition-colors ${active ? 'bg-white/10 text-white border-b-2 border-blue-400 -mb-px' : 'text-white/55 hover:text-white hover:bg-white/5'}`}>
                  <TabIcon size={16} />
                  <span>{tab.label}</span>
                  {tab.id === 'history' ? <span className="text-white/50 text-xs">({versions.length})</span> : null}
                </button>
              );
            })}
          </div>
        </div>

        {versionLogTab === 'overview' && (
          currentVersion ? (
            <div className="relative group">
              <div className="absolute -inset-0.5 bg-gradient-to-r from-purple-500 to-pink-600 rounded-3xl opacity-0 group-hover:opacity-40 transition-opacity duration-500 blur-sm" style={{ pointerEvents: 'none' }} />
              <div className="relative bg-gradient-to-r from-purple-500/10 to-pink-500/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
                <div className="flex items-center justify-between flex-wrap gap-3 mb-2">
                  <h3 className="text-xl font-bold text-white">{currentVersion.version_number}</h3>
                  <span className="px-3 py-1 bg-green-500/20 text-green-300 text-xs font-semibold rounded-full">{p('active')}</span>
                </div>
                <p className="text-white/70 text-sm">{currentVersion.description || p('no_description')}</p>
                <div className="flex flex-wrap gap-4 mt-3 text-xs text-white/50">
                  <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{new Date(currentVersion.installation_date || currentVersion.release_date).toLocaleDateString(i18n.language)}</span>
                  <span className="flex items-center gap-1"><User className="w-3 h-3" />{currentVersion.installed_by || p('system')}</span>
                </div>
              </div>
            </div>
          ) : (
            <p className="text-white/50 text-sm text-center py-6">{p('no_version_recorded')}</p>
          )
        )}

        {versionLogTab === 'add' && showAddForm && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block w-full text-sm text-white/70 mb-2 font-medium text-right">{p('version_number')} <span className="qawaem-required-mark">*</span></label>
              <input type="text" placeholder={p('version_number_placeholder')} value={newVersion.version_number} onChange={(e) => setNewVersion({ ...newVersion, version_number: e.target.value })} className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40" />
            </div>
            <div>
              <label className="block w-full text-sm text-white/70 mb-2 font-medium text-right">{p('release_date')}</label>
              <input type="date" value={newVersion.release_date} onChange={(e) => setNewVersion({ ...newVersion, release_date: e.target.value })} className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-xl text-white" />
            </div>
            <div className="md:col-span-3">
              <label className="block w-full text-sm text-white/70 mb-2 font-medium text-right">{p('description')}</label>
              <input type="text" value={newVersion.description} onChange={(e) => setNewVersion({ ...newVersion, description: e.target.value })} className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40" />
            </div>
            <div className="md:col-span-3">
              <label className="block w-full text-sm text-white/70 mb-2 font-medium text-right">{p('changelog')}</label>
              <textarea rows="3" value={newVersion.changelog} onChange={(e) => setNewVersion({ ...newVersion, changelog: e.target.value })} className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40" />
            </div>
            <div className="md:col-span-3 flex gap-2 justify-end">
              <button type="button" onClick={() => setShowAddForm(false)} className="px-4 py-2 rounded-xl bg-white/10 border border-white/20 text-white hover:bg-white/15">{p('cancel')}</button>
              <button type="button" onClick={handleAddVersion} className="px-4 py-2 rounded-xl bg-gradient-to-r from-purple-500 to-pink-600 text-white font-medium">{p('add')}</button>
            </div>
          </div>
        )}

        {versionLogTab === 'history' && (
          isLoading ? (
            <div className="flex items-center justify-center py-16">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-purple-500" />
            </div>
          ) : versions.length === 0 ? (
            <div className="text-center py-12">
              <Package className="w-12 h-12 mx-auto mb-4 text-white/30" />
              <p className="text-white/60">{p('no_version_recorded')}</p>
            </div>
          ) : (
            <div className="space-y-3 max-h-80 overflow-y-auto">
              {versions
                .sort((a, b) => new Date(b.installation_date || b.release_date) - new Date(a.installation_date || a.release_date))
                .map((version, index) => {
                  const isCurrent = currentVersion && currentVersion.id === version.id;
                  return (
                    <div key={version.id || index} className={`bg-white/5 rounded-xl p-4 border ${isCurrent ? 'border-purple-400/50 ring-1 ring-purple-500/30' : 'border-white/10'}`}>
                      <div className="flex justify-between items-start gap-2 flex-wrap mb-2">
                        <div className="flex items-center gap-2">
                          <Tag className="w-4 h-4 text-purple-400" />
                          <span className="text-white font-bold">{version.version_number}</span>
                          {isCurrent && <span className="px-2 py-0.5 bg-purple-500/20 text-purple-300 text-xs rounded-full">{p('current_badge')}</span>}
                        </div>
                        <span className="text-white/50 text-xs">{new Date(version.release_date).toLocaleDateString(i18n.language)}</span>
                      </div>
                      {version.description && <p className="text-white/70 text-sm mb-2">{version.description}</p>}
                      {version.changelog && <p className="text-white/50 text-xs whitespace-pre-wrap bg-white/5 rounded-lg p-2">{version.changelog}</p>}
                    </div>
                  );
                })}
            </div>
          )
        )}

        <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
          <p className="text-blue-300 text-sm"><strong>{p('note')}:</strong> {p('note_version_compliance')}</p>
        </div>
      </div>
    </ComplianceModalShell>
  );
};

const ParameterComparisonTool = ({ isOpen, onClose }) => {
  const { t, i18n } = useTranslation();
  const p = (key, opts) => t(`modules.comptabilite.pages.conformiteAuditPage.${key}`, opts);
  const [parameters, setParameters] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [filterType, setFilterType] = useState('');
  const [showOnlyModified, setShowOnlyModified] = useState(false);
  const [paramComparisonTab, setParamComparisonTab] = useState('filters');

  const parameterTypes = [
    { value: '', label: p('all_types') },
    { value: 'formula', label: p('type_formula') },
    { value: 'account_structure', label: p('type_account_structure') },
    { value: 'code_length', label: p('type_code_length') },
    { value: 'validation_rules', label: p('type_validation_rules') },
    { value: 'tax_rates', label: p('type_tax_rates') },
    { value: 'display', label: p('type_display') },
    { value: 'security', label: p('type_security') },
    { value: 'other', label: p('type_other') }
  ];

  useEffect(() => {
    if (isOpen) {
      loadParameters();
      setParamComparisonTab('filters');
    }
  }, [isOpen]);

  const loadParameters = async () => {
    setIsLoading(true);
    try {
      const data = await getParameterComparison();
      setParameters(data);
    } catch (error) {
      logger.error('Error loading parameter comparison:', error);
    }
    setIsLoading(false);
  };

  const handleResetToDefault = async (parameterId, parameterName) => {
    if (!window.confirm(p('confirm_reset_parameter'))) {
      return;
    }

    try {
      logger.log('Reset parameter:', parameterId, parameterName);
      alert(p('alert_parameter_reset_success'));
      await loadParameters();
    } catch (error) {
      logger.error('Error resetting parameter:', error);
      alert(p('alert_reset_error'));
    }
  };

  const isModified = (param) => {
    return param.current_value !== param.default_value;
  };

  const filteredParameters = parameters.filter(param => {
    if (filterType && param.parameter_type !== filterType) {
      return false;
    }
    if (showOnlyModified && !isModified(param)) {
      return false;
    }
    return true;
  });

  const paramComparisonTabs = useMemo(() => ([
    { id: 'filters', label: p('search_filters'), icon: Filter },
    { id: 'parameters', label: p('parameters_title', { count: filteredParameters.length }), icon: List },
  ]), [p, filteredParameters.length]);

  const getParameterTypeColor = (type) => {
    const colors = {
      formula: 'bg-blue-500/20 text-blue-300 border-blue-500/30',
      account_structure: 'bg-purple-500/20 text-purple-300 border-purple-500/30',
      code_length: 'bg-green-500/20 text-green-300 border-green-500/30',
      validation_rules: 'bg-orange-500/20 text-orange-300 border-orange-500/30',
      tax_rates: 'bg-red-500/20 text-red-300 border-red-500/30',
      display: 'bg-indigo-500/20 text-indigo-300 border-indigo-500/30',
      security: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30',
      other: 'bg-gray-500/20 text-gray-300 border-gray-500/30'
    };
    return colors[type] || 'bg-gray-500/20 text-gray-300 border-gray-500/30';
  };

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4" onClick={onClose} role="presentation">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-gradient-to-br from-slate-800 via-orange-800/30 to-slate-800 backdrop-blur-lg border-2 border-orange-400/60 rounded-2xl max-h-[90vh] flex flex-col overflow-hidden" 
        style={{ boxShadow: '0 0 40px rgba(251, 146, 60, 0.4), 0 0 80px rgba(251, 146, 60, 0.2)', width: '90%', maxWidth: '1200px' }}
      >
          {/* Fixed Header */}
          <div className="flex items-center justify-between p-6 border-b border-white/10">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-white/20 backdrop-blur-lg flex items-center justify-center">
                <Settings className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white">
                  {p('param_comparison_title')}
                </h2>
                <p className="text-white/60 text-sm mt-1">
                  {p('param_comparison_subtitle')}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {/* Stats */}
              <div className="flex items-center gap-4 px-4 py-2 bg-white/10 rounded-xl border border-white/20">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  <span className="text-sm font-medium text-white/80">
                    {p('stats_default', { count: parameters.filter((param) => !isModified(param)).length })}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-orange-400" />
                  <span className="text-sm font-medium text-white/80">
                    {p('stats_modified', { count: parameters.filter((param) => isModified(param)).length })}
                  </span>
                </div>
              </div>
              <button
                onClick={loadParameters}
                className="px-4 py-2.5 rounded-xl bg-gradient-to-r from-blue-500 to-blue-700 text-white hover:from-blue-600 hover:to-blue-800 transition-all duration-200 font-semibold flex items-center gap-2 shadow-lg shadow-blue-500/30"
              >
                <RefreshCw className="w-4 h-4" />
                {p('refresh')}
              </button>
              <button onClick={onClose} className="text-white/70 hover:text-white transition-colors">
                <X size={24} />
              </button>
            </div>
          </div>

          {/* Scrollable Content */}
          <div className="overflow-y-auto p-6 flex-1">
            <div className="space-y-4">
              <div className="shrink-0 border-b border-white/10 pb-0">
                <div className="flex flex-wrap gap-1" role="tablist">
                  {paramComparisonTabs.map((tab) => {
                    const TabIcon = tab.icon;
                    const active = paramComparisonTab === tab.id;
                    return (
                      <button key={tab.id} type="button" role="tab" aria-selected={active} onClick={() => setParamComparisonTab(tab.id)} className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-t-xl text-sm font-semibold transition-colors ${active ? 'bg-white/10 text-white border-b-2 border-orange-400 -mb-px' : 'text-white/55 hover:text-white hover:bg-white/5'}`}>
                        <TabIcon size={16} />
                        <span>{tab.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {paramComparisonTab === 'filters' && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">
                      {p('parameter_type')}
                    </label>
                    <select
                      value={filterType}
                      onChange={(e) => setFilterType(e.target.value)}
                      className="w-full bg-white/10 border border-white/20 text-white rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-orange-500 transition-all"
                    >
                      {parameterTypes.map(type => (
                        <option key={type.value} value={type.value} className="bg-slate-800">{type.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="flex items-end md:col-span-2">
                    <label className="flex items-center gap-2 cursor-pointer w-full p-3 bg-white/5 rounded-xl border border-white/10 hover:bg-white/10 transition-colors">
                      <input
                        type="checkbox"
                        checked={showOnlyModified}
                        onChange={(e) => setShowOnlyModified(e.target.checked)}
                        className="w-4 h-4 text-orange-600 rounded focus:ring-2 focus:ring-orange-500"
                      />
                      <span className="text-sm font-medium text-white/80 flex items-center gap-2">
                        <AlertCircle className="w-4 h-4 text-orange-400" />
                        {p('modified_only')}
                      </span>
                    </label>
                  </div>
                </div>
              )}

              {paramComparisonTab === 'parameters' && (
                isLoading ? (
                  <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500" />
                  </div>
                ) : filteredParameters.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-64 text-white/50">
                    <Settings className="w-16 h-16 mb-4 opacity-50" />
                    <p className="text-lg font-medium">{p('no_parameters_found')}</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {filteredParameters.map((param, index) => {
                      const modified = isModified(param);
                      return (
                        <motion.div
                          key={param.id || index}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.03 }}
                          className={`bg-white/5 border rounded-xl p-4 ${
                            modified ? 'border-orange-400/50 bg-orange-500/10' : 'border-white/10'
                          }`}
                        >
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-2">
                                <h3 className="text-base font-semibold text-white">
                                  {param.parameter_name}
                                </h3>
                                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full border ${getParameterTypeColor(param.parameter_type)}`}>
                                  {param.parameter_type}
                                </span>
                                {modified && (
                                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-orange-500/20 text-orange-300 border border-orange-500/30 text-xs font-semibold rounded-full">
                                    <AlertCircle className="w-3 h-3" />
                                    {p('modified_badge')}
                                  </span>
                                )}
                              </div>
                              {param.description && (
                                <p className="text-sm text-white/60">
                                  {param.description}
                                </p>
                              )}
                            </div>
                            {modified && (
                              <button
                                type="button"
                                onClick={() => handleResetToDefault(param.id, param.parameter_name)}
                                className="px-3 py-1.5 text-sm bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors flex items-center gap-2"
                              >
                                <RefreshCw className="w-3.5 h-3.5" />
                                {p('reset')}
                              </button>
                            )}
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
                            <div className="border border-white/10 rounded-lg p-3 bg-white/5 md:col-span-1">
                              <div className="flex items-center gap-2 mb-2">
                                <CheckCircle className="w-3.5 h-3.5 text-white/50" />
                                <span className="text-xs font-medium text-white/60 uppercase">
                                  {p('default_value')}
                                </span>
                              </div>
                              <div className="text-sm font-mono text-white bg-white/10 rounded p-2 border border-white/10">
                                {param.default_value || p('not_defined')}
                              </div>
                            </div>
                            <div className={`border rounded-lg p-3 md:col-span-2 ${modified ? 'bg-orange-500/10 border-orange-500/30' : 'bg-green-500/10 border-green-500/30'}`}>
                              <div className="flex items-center gap-2 mb-2">
                                {modified ? (
                                  <AlertCircle className="w-3.5 h-3.5 text-orange-400" />
                                ) : (
                                  <CheckCircle className="w-3.5 h-3.5 text-green-400" />
                                )}
                                <span className={`text-xs font-medium uppercase ${modified ? 'text-orange-300' : 'text-green-300'}`}>
                                  {p('current_value')}
                                </span>
                              </div>
                              <div className={`text-sm font-mono font-semibold rounded p-2 border ${
                                modified
                                  ? 'text-orange-200 bg-white/10 border-orange-500/30'
                                  : 'text-green-200 bg-white/10 border-green-500/30'
                              }`}>
                                {param.current_value || p('not_defined')}
                              </div>
                            </div>
                          </div>
                          {(param.last_modified_by || param.last_modified_at) && (
                            <div className="flex items-center gap-4 mt-3 pt-3 border-t border-white/10 text-xs text-white/50">
                              {param.last_modified_at && (
                                <span className="flex items-center gap-1">
                                  <Calendar className="w-3 h-3" />
                                  {p('modified_on')}: {new Date(param.last_modified_at).toLocaleDateString(i18n.language)}
                                </span>
                              )}
                              {param.last_modified_by && (
                                <span className="flex items-center gap-1">
                                  <User className="w-3 h-3" />
                                  {p('by')}: {param.last_modified_by}
                                </span>
                              )}
                            </div>
                          )}
                        </motion.div>
                      );
                    })}
                  </div>
                )
              )}
            </div>
          </div>

          {/* Fixed Footer */}
          <div className="p-4 border-t border-white/10 bg-white/5 flex items-center justify-between">
            <div className="text-sm text-white/60">
              {p('parameters_displayed', { count: filteredParameters.length })}
            </div>
            <div className="flex items-center gap-2 text-xs text-white/50">
              <ArrowRightLeft className="w-4 h-4" />
              <span>{p('compliance_article4')}</span>
            </div>
          </div>
        </motion.div>
    </div>,
    document.body
  );
};

const DEFAULT_COMPLIANCE_DATA = {
  overview: {
    complianceScore: 92,
    totalChecks: 12,
    passedChecks: 10,
    failedChecks: 0,
    pendingChecks: 0,
    warningChecks: 2,
    lastAudit: '15/12/2024',
    nextAudit: '15/06/2025',
  },
  categories: [
    {
      id: 'irreversibility',
      titleKey: 'cat_irreversibility_title',
      descriptionKey: 'cat_irreversibility_desc',
      status: 'compliant',
      score: 100,
      checks: [
        { nameKey: 'check_timestamp_name', descriptionKey: 'check_timestamp_desc', status: 'passed' },
        { nameKey: 'check_sequential_numbering_name', descriptionKey: 'check_sequential_numbering_desc', status: 'passed' },
        { nameKey: 'check_period_lock_name', descriptionKey: 'check_period_lock_desc', status: 'passed' },
      ],
    },
    {
      id: 'traceability',
      titleKey: 'cat_traceability_title',
      descriptionKey: 'cat_traceability_desc',
      status: 'compliant',
      score: 95,
      checks: [
        { nameKey: 'check_audit_trail_name', descriptionKey: 'check_audit_trail_desc', status: 'passed' },
        { nameKey: 'check_electronic_journal_name', descriptionKey: 'check_electronic_journal_desc', status: 'passed' },
        { nameKey: 'check_user_identification_name', descriptionKey: 'check_user_identification_desc', status: 'warning' },
      ],
    },
    {
      id: 'auditability',
      titleKey: 'cat_auditability_title',
      descriptionKey: 'cat_auditability_desc',
      status: 'warning',
      score: 88,
      checks: [
        { nameKey: 'check_auto_reports_name', descriptionKey: 'check_auto_reports_desc', status: 'passed' },
        { nameKey: 'check_debit_credit_balance_name', descriptionKey: 'check_debit_credit_balance_desc', status: 'passed' },
        { nameKey: 'check_digital_signatures_name', descriptionKey: 'check_digital_signatures_desc', status: 'warning' },
      ],
    },
    {
      id: 'conservation',
      titleKey: 'cat_conservation_title',
      descriptionKey: 'cat_conservation_desc',
      status: 'compliant',
      score: 100,
      checks: [
        { nameKey: 'check_retention_10y_name', descriptionKey: 'check_retention_10y_desc', status: 'passed' },
        { nameKey: 'check_auto_backups_name', descriptionKey: 'check_auto_backups_desc', status: 'passed' },
        { nameKey: 'check_redundant_locations_name', descriptionKey: 'check_redundant_locations_desc', status: 'passed' },
      ],
    },
  ],
  auditLog: [
    { actionKey: 'audit_monthly_close_validated', userKey: 'user_main_accountant', moduleKey: 'module_closing', date: '28/12/2024 14:32', status: 'success' },
    { actionKey: 'audit_export_journal', userKey: 'user_cfo', moduleKey: 'module_entries', date: '20/12/2024 09:15', status: 'success' },
    { actionKey: 'audit_vat_param_change', userKey: 'user_admin', moduleKey: 'module_config', date: '15/12/2024 16:48', status: 'warning' },
    { actionKey: 'audit_auto_backup', userKey: 'system', moduleKey: 'module_backup', date: '15/12/2024 02:00', status: 'success' },
    { actionKey: 'audit_compliance_launched', userKey: 'user_internal_auditor', moduleKey: 'module_compliance', date: '15/12/2024 11:00', status: 'success' },
  ],
  backup: {
    lastBackup: '15/12/2024 02:00',
    nextBackup: '16/12/2024 02:00',
    backupSize: '2,4 Go',
    retention: '10 ans',
    locationKeys: ['backup_location_local', 'backup_location_cloud_algiers', 'backup_location_cloud_oran'],
  },
};

const mergeComplianceData = (apiData) => {
  const base = { ...DEFAULT_COMPLIANCE_DATA };
  if (!apiData) return base;
  const hasCategories = (apiData.categories || []).length > 0;
  const hasAudit = (apiData.auditLog || []).length > 0;
  const hasOverview = (apiData.overview?.totalChecks || 0) > 0;
  return {
    overview: {
      ...base.overview,
      ...apiData.overview,
      complianceScore: hasOverview ? apiData.overview.complianceScore : base.overview.complianceScore,
      lastAudit: apiData.overview?.lastAudit && apiData.overview.lastAudit !== 'N/A'
        ? apiData.overview.lastAudit : base.overview.lastAudit,
    },
    categories: hasCategories ? apiData.categories : base.categories,
    auditLog: hasAudit ? apiData.auditLog : base.auditLog,
    backup: {
      ...base.backup,
      ...apiData.backup,
      locationKeys: (apiData.backup?.locationKeys || []).length
        ? apiData.backup.locationKeys
        : (apiData.backup?.locations || []).length
          ? apiData.backup.locations
          : base.backup.locationKeys,
      lastBackup: apiData.backup?.lastBackup && apiData.backup.lastBackup !== 'N/A'
        ? apiData.backup.lastBackup : base.backup.lastBackup,
    },
  };
};

const EmptyTabState = ({ icon: Icon, title, description }) => (
  <div className="text-center py-16 bg-white/5 rounded-xl border-2 border-dashed border-white/10">
    <Icon className="w-12 h-12 mx-auto mb-4 text-white/30" />
    <p className="text-white/80 font-medium">{title}</p>
    <p className="text-white/50 text-sm mt-2 max-w-md mx-auto">{description}</p>
  </div>
);

const ConformiteAuditPage = () => {
  const { t } = useTranslation();
  const p = (key, opts) => t(`modules.comptabilite.pages.conformiteAuditPage.${key}`, opts);

  const mainTabs = useMemo(() => [
    { id: 'controles', label: p('tab_controls'), icon: Shield },
    { id: 'journal', label: p('tab_audit_journal'), icon: Users },
    { id: 'sauvegardes', label: p('tab_backups'), icon: HardDrive },
  ], [t]);

  const reportFormatOptions = useMemo(() => [
    { value: 'pdf_signed', label: p('format_pdf_signed') },
    { value: 'pdf', label: p('format_pdf') },
    { value: 'xlsx', label: p('format_xlsx') },
    { value: 'html', label: p('format_html') },
  ], [t]);

  const reportPeriodOptions = useMemo(() => [
    { value: 'full_year', label: p('period_full_year') },
    { value: 'quarter', label: p('period_quarter') },
    { value: 'month', label: p('period_month') },
    { value: 'custom', label: p('period_custom') },
  ], [t]);

  const reportItems = useMemo(() => [
    p('report_item_compliance_score'),
    p('report_item_electronic_journal'),
    p('report_item_version_history'),
    p('report_item_integrity_checks'),
    p('report_item_passed_failed_controls'),
    p('report_item_adjustments'),
    p('report_item_backup_locations'),
    p('report_item_audit_trail'),
  ], [t]);

  const [complianceData, setComplianceData] = useState(DEFAULT_COMPLIANCE_DATA);
  const [activeTab, setActiveTab] = useState('controles');
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [showJournalElectronique, setShowJournalElectronique] = useState(false);
  const [showVersionLog, setShowVersionLog] = useState(false);
  const [showParameterComparison, setShowParameterComparison] = useState(false);
  const [showRapportAudit, setShowRapportAudit] = useState(false);
  const [showLancerAudit, setShowLancerAudit] = useState(false);
  const [showSauvegarderMaintenant, setShowSauvegarderMaintenant] = useState(false);
  const [showAuditRapide, setShowAuditRapide] = useState(false);
  const [reportFormat, setReportFormat] = useState('pdf_signed');
  const [reportPeriod, setReportPeriod] = useState('full_year');
  const [auditRunning, setAuditRunning] = useState(false);
  const [fiscalYearLabel, setFiscalYearLabel] = useState('');

  const loadComplianceData = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const apiData = await getComplianceChecks();
      setComplianceData(mergeComplianceData(apiData));
    } catch (error) {
      logger.error('Error loading compliance data:', error);
      setLoadError(p('load_error_fallback'));
      setComplianceData(DEFAULT_COMPLIANCE_DATA);
    }
    setIsLoading(false);
  }, [p]);

  useEffect(() => {
    fetchCurrentFiscalYearContext()
      .then(({ label }) => { if (label) setFiscalYearLabel(label); })
      .catch(() => {});
    loadComplianceData();
  }, [loadComplianceData]);

  const handleExportAuditReport = async () => {
    try {
      const rows = [
        { [p('export_col_indicator')]: p('export_indicator_fiscal_year', { defaultValue: 'السنة المالية' }), [p('export_col_value')]: fiscalYearLabel || resolveFiscalYearLabel() },
        { [p('export_col_indicator')]: p('export_indicator_compliance_score'), [p('export_col_value')]: `${complianceData.overview.complianceScore}%` },
        { [p('export_col_indicator')]: p('export_indicator_passed'), [p('export_col_value')]: complianceData.overview.passedChecks },
        { [p('export_col_indicator')]: p('export_indicator_warnings'), [p('export_col_value')]: complianceData.overview.warningChecks },
        { [p('export_col_indicator')]: p('export_indicator_last_audit'), [p('export_col_value')]: complianceData.overview.lastAudit },
        ...(complianceData.categories || []).flatMap((cat) =>
          (cat.checks || []).map((c) => ({
            [p('export_col_category')]: p(cat.titleKey || cat.title),
            [p('export_col_control')]: p(c.nameKey || c.name),
            [p('export_col_status')]: c.status === 'passed' ? p('export_status_passed') : c.status === 'warning' ? p('export_status_warning') : p('export_status_failed'),
          }))
        ),
      ];
      await generateReportPDF(p('export_report_title'), rows, { orientation: 'landscape' });
      setShowRapportAudit(false);
    } catch (error) {
      logger.error('Export audit report:', error);
    }
  };

  const handleRunFullAudit = async () => {
    setAuditRunning(true);
    try {
      await loadComplianceData();
      setShowLancerAudit(false);
      setActiveTab('controles');
    } finally {
      setAuditRunning(false);
    }
  };

  const statsCards = useMemo(() => [
    { title: p('stat_compliance_score'), value: `${complianceData.overview.complianceScore}%`, icon: Shield, color: 'from-green-500 to-emerald-600' },
    { title: p('stat_passed_checks'), value: complianceData.overview.passedChecks, icon: CheckCircle, color: 'from-blue-500 to-cyan-600' },
    { title: p('stat_warnings'), value: complianceData.overview.warningChecks, icon: AlertTriangle, color: 'from-orange-500 to-red-600' },
    { title: p('stat_last_audit'), value: complianceData.overview.lastAudit, icon: Calendar, color: 'from-purple-500 to-pink-600' },
    { title: p('stat_next_audit'), value: complianceData.overview.nextAudit || '—', icon: Clock, color: 'from-indigo-500 to-purple-600' },
  ], [p, complianceData.overview]);

  const renderCategoryCard = (category, index) => (
    <motion.div
      key={category.id}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
      className="relative group"
    >
      <div className={`absolute -inset-0.5 rounded-3xl opacity-0 group-hover:opacity-40 transition-opacity duration-500 blur-sm bg-gradient-to-r ${
        category.status === 'compliant' ? 'from-green-500 to-emerald-600'
          : category.status === 'warning' ? 'from-orange-500 to-yellow-600'
            : 'from-red-500 to-pink-600'
      }`} style={{ pointerEvents: 'none' }} />
      <div className="relative bg-white/10 backdrop-blur-lg rounded-3xl border border-white/20 overflow-hidden">
        <div className={`p-4 border-b border-white/10 ${
          category.status === 'compliant' ? 'bg-gradient-to-r from-green-600/20 to-emerald-600/20'
            : category.status === 'warning' ? 'bg-gradient-to-r from-orange-600/20 to-yellow-600/20'
              : 'bg-gradient-to-r from-red-600/20 to-pink-600/20'
        }`}>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center space-x-3 min-w-0">
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                category.status === 'compliant' ? 'bg-gradient-to-r from-green-500 to-emerald-600'
                  : category.status === 'warning' ? 'bg-gradient-to-r from-orange-500 to-yellow-600'
                    : 'bg-gradient-to-r from-red-500 to-pink-600'
              }`}>
                {category.status === 'compliant' && <CheckCircle className="h-5 w-5 text-white" />}
                {category.status === 'warning' && <AlertTriangle className="h-5 w-5 text-white" />}
                {category.status === 'failed' && <X className="h-5 w-5 text-white" />}
              </div>
              <div className="min-w-0">
                <h3 className="text-lg font-semibold text-white">{p(category.titleKey || category.title)}</h3>
                {category.descriptionKey ? <p className="text-white/50 text-xs mt-1 truncate">{p(category.descriptionKey)}</p> : category.description ? <p className="text-white/50 text-xs mt-1 truncate">{category.description}</p> : null}
              </div>
            </div>
            <div className="text-right shrink-0">
              <div className={`text-2xl font-bold ${
                category.status === 'compliant' ? 'text-green-400'
                  : category.status === 'warning' ? 'text-orange-400' : 'text-red-400'
              }`}>
                {category.score}%
              </div>
              <span className={`px-3 py-1 rounded-full text-xs ${
                category.status === 'compliant' ? 'bg-green-500/20 text-green-300'
                  : category.status === 'warning' ? 'bg-orange-500/20 text-orange-300'
                    : 'bg-red-500/20 text-red-300'
              }`}>
                {category.status === 'compliant' ? p('status_compliant') : category.status === 'warning' ? p('status_warning') : p('status_non_compliant')}
              </span>
            </div>
          </div>
        </div>
        <div className="p-4 space-y-2">
          {(category.checks || []).map((check, checkIndex) => (
            <div key={checkIndex} className="flex items-center justify-between p-3 rounded-xl hover:bg-white/5 transition-colors gap-2">
              <div className="flex items-center space-x-3 min-w-0">
                <div className={`w-3 h-3 rounded-full shrink-0 ${
                  check.status === 'passed' ? 'bg-green-500' : check.status === 'warning' ? 'bg-orange-500' : 'bg-red-500'
                }`} />
                <div className="min-w-0">
                  <p className="text-white text-sm font-medium">{p(check.nameKey || check.name)}</p>
                  <p className="text-white/50 text-xs truncate">{check.descriptionKey ? p(check.descriptionKey) : check.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </motion.div>
  );

  const renderTabContent = () => {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center py-24">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500" />
        </div>
      );
    }

    if (activeTab === 'controles') {
      return (
        <div className="space-y-4">
          {loadError && (
            <div className="flex items-center gap-2 bg-orange-500/10 border border-orange-500/30 rounded-xl p-3 text-orange-200 text-sm">
              <AlertCircle size={16} />
              {loadError}
            </div>
          )}
          {(complianceData.categories || []).length === 0 ? (
            <EmptyTabState icon={Shield} title={p('empty_controls_title')} description={p('empty_controls_desc')} />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {complianceData.categories.map(renderCategoryCard)}
            </div>
          )}
        </div>
      );
    }

    if (activeTab === 'journal') {
      return (
        <div className="space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <h3 className="text-lg font-semibold text-white">{p('audit_journal_title')}</h3>
              <p className="text-white/60 text-sm">{p('audit_journal_subtitle')}</p>
            </div>
            <button type="button" onClick={() => setShowJournalElectronique(true)} className="text-blue-400 hover:text-blue-300 text-sm font-medium flex items-center gap-1">
              <FileText size={14} />
              {p('btn_journal_electronique_short')}
            </button>
          </div>
          {(complianceData.auditLog || []).length === 0 ? (
            <EmptyTabState icon={Users} title={p('empty_journal_title')} description={p('empty_journal_desc')} />
          ) : (
            <div className="space-y-2">
              {complianceData.auditLog.map((log, index) => (
                <div key={index} className="flex items-center justify-between p-4 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 transition-colors flex-wrap gap-2">
                  <div className="flex items-center space-x-3 min-w-0">
                    <div className={`w-3 h-3 rounded-full shrink-0 ${
                      log.status === 'success' ? 'bg-green-500' : log.status === 'warning' ? 'bg-orange-500' : 'bg-red-500'
                    }`} />
                    <div className="min-w-0">
                      <p className="text-white text-sm font-medium">{p(log.actionKey || log.action)}</p>
                      <p className="text-white/50 text-xs">{p(log.userKey || log.user)} • {p(log.moduleKey || log.module)}</p>
                    </div>
                  </div>
                  <p className="text-white/60 text-xs shrink-0">{log.date}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      );
    }

    if (activeTab === 'sauvegardes') {
      const backup = complianceData.backup || {};
      return (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              { label: p('backup_last'), value: backup.lastBackup, color: 'text-green-400' },
              { label: p('backup_next'), value: backup.nextBackup, color: 'text-blue-400' },
              { label: p('backup_data_size'), value: backup.backupSize, color: 'text-white' },
              { label: p('backup_retention'), value: backup.retention, color: 'text-white' },
            ].map((item, i) => (
              <div key={i} className="relative group">
                <div className="absolute -inset-0.5 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-3xl opacity-0 group-hover:opacity-40 transition-opacity duration-500 blur-sm" style={{ pointerEvents: 'none' }} />
                <div className="relative bg-white/10 backdrop-blur-lg rounded-3xl p-5 border border-white/20">
                  <p className="text-white/60 text-sm">{item.label}</p>
                  <p className={`text-lg font-bold mt-1 ${item.color}`}>{item.value || p('na')}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="bg-white/5 rounded-xl border border-white/10 p-6">
            <h4 className="text-white font-semibold mb-3">{p('backup_locations_title')}</h4>
            {(backup.locationKeys || backup.locations || []).length === 0 ? (
              <p className="text-white/50 text-sm">{p('no_backup_locations')}</p>
            ) : (
              <div className="space-y-2">
                {(backup.locationKeys || backup.locations).map((location, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <div className="w-2 h-2 rounded-full bg-green-500 shrink-0" />
                    <span className="text-white/70 text-sm">
                      {backup.locationKeys ? p(location) : location}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={() => setShowSauvegarderMaintenant(true)}
            className="w-full bg-gradient-to-r from-cyan-600 to-blue-600 text-white py-3 rounded-xl font-medium hover:from-cyan-700 hover:to-blue-700 transition-all flex items-center justify-center space-x-2 shadow-lg shadow-blue-500/30"
          >
            <Database size={16} />
            <span>{p('backup_now')}</span>
          </button>
        </div>
      );
    }

    return null;
  };

  return (
    <>
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-xl font-semibold text-white">{p('title')}</h2>
          <p className="text-white/60 text-sm">{p('subtitle')} — {fiscalYearLabel || resolveFiscalYearLabel()}</p>
        </div>
        <div className="flex gap-3">
          <button 
            onClick={() => setShowJournalElectronique(true)}
            className="bg-white/10 backdrop-blur-lg border border-white/20 text-white px-4 py-2 rounded-xl font-medium hover:bg-white/15 transition-all duration-300 flex items-center space-x-2"
          >
            <FileText size={16} />
            <span>{p('btn_journal_electronique')}</span>
          </button>
          <button 
            onClick={() => setShowVersionLog(true)}
            className="bg-white/10 backdrop-blur-lg border border-white/20 text-white px-4 py-2 rounded-xl font-medium hover:bg-white/15 transition-all duration-300 flex items-center space-x-2"
          >
            <Package size={16} />
            <span>{p('btn_versions')}</span>
          </button>
          <button 
            onClick={() => setShowRapportAudit(true)}
            className="bg-white/10 backdrop-blur-lg border border-white/20 text-white px-4 py-2 rounded-xl font-medium hover:bg-white/15 transition-all duration-300 flex items-center space-x-2"
          >
            <Download size={16} />
            <span>{p('btn_audit_report')}</span>
          </button>
          <button 
            onClick={() => setShowLancerAudit(true)}
            className="bg-gradient-to-r from-blue-500 to-blue-700 text-white px-4 py-2 rounded-xl font-medium hover:from-blue-600 hover:to-blue-800 transition-all duration-300 flex items-center space-x-2"
          >
            <Shield size={16} />
            <span>{p('btn_launch_audit')}</span>
          </button>
        </div>
      </div>

      <div className="space-y-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {statsCards.slice(0, 4).map((stat, i) => (
            <div key={i} className="relative group">
              <div className={`absolute -inset-0.5 bg-gradient-to-r ${stat.color} rounded-3xl opacity-0 group-hover:opacity-50 transition-opacity duration-500 blur-sm`} style={{ pointerEvents: 'none' }} />
              <div className="relative bg-white/10 backdrop-blur-lg rounded-3xl p-5 border border-white/20">
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <h3 className="text-white/60 text-xs font-medium truncate">{stat.title}</h3>
                    <p className="text-lg font-bold text-white mt-1 truncate">{stat.value}</p>
                  </div>
                  <div className={`p-2.5 rounded-xl bg-gradient-to-r ${stat.color} shrink-0`}>
                    <stat.icon className="w-5 h-5 text-white" />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-2xl overflow-hidden">
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border-b border-white/10 m-2 mb-0">
            <div className="flex space-x-1 overflow-x-auto hide-scrollbar">
              {mainTabs.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 whitespace-nowrap flex-shrink-0 ${
                    activeTab === tab.id
                      ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30'
                      : 'text-white/80 hover:bg-white/10 hover:text-white'
                  }`}
                >
                  <tab.icon className="h-5 w-5" />
                  <span>{tab.label}</span>
                </button>
              ))}
            </div>
          </div>
          <div className="p-6">{renderTabContent()}</div>
        </div>
      </div>

      <ComplianceModalShell
        open={showRapportAudit}
        onClose={() => setShowRapportAudit(false)}
        title={p('report_modal_title')}
        Icon={Download}
        iconClass="text-orange-400"
        footer={(
          <>
            <button type="button" onClick={() => setShowRapportAudit(false)} className="px-4 py-2.5 rounded-xl bg-white/10 border border-white/20 text-white hover:bg-white/15 font-medium">{p('close')}</button>
            <button type="button" onClick={handleExportAuditReport} className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-orange-500 to-red-600 text-white font-semibold shadow-lg flex items-center gap-2">
              <Download size={16} />
              {p('generate_audit_report')}
            </button>
          </>
        )}
      >
        <div className="space-y-4">
          <div className="bg-orange-500/10 border border-orange-500/30 rounded-xl p-4">
            <p className="text-orange-300 text-sm">{p('report_compliance_note')}</p>
          </div>
          <div className="space-y-4 pb-6 border-b border-white/10">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              {reportItems.map((item, idx) => (
                <label key={idx} className="flex items-center space-x-3 text-white text-sm bg-white/5 rounded-lg p-3 border border-white/10">
                  <input type="checkbox" defaultChecked className="rounded accent-orange-500" />
                  <span>{item}</span>
                </label>
              ))}
            </div>
          </div>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('output_format')}</label>
                <CustomSelect value={reportFormat} onChange={setReportFormat} options={reportFormatOptions} autoSelect={false} />
              </div>
              <div>
                <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('covered_period')}</label>
                <CustomSelect value={reportPeriod} onChange={setReportPeriod} options={reportPeriodOptions} autoSelect={false} />
              </div>
              <div className="flex items-end">
                <label className="flex items-center space-x-3 text-white text-sm">
                  <input type="checkbox" defaultChecked className="rounded accent-blue-500" />
                  <span>{p('include_digital_signature')}</span>
                </label>
              </div>
            </div>
          </div>
        </div>
      </ComplianceModalShell>

      <ComplianceModalShell
        open={showLancerAudit}
        onClose={() => setShowLancerAudit(false)}
        title={p('launch_audit_modal_title')}
        Icon={Shield}
        iconClass="text-blue-400"
        maxWidth="960px"
        footer={(
          <>
            <button type="button" onClick={() => { setShowLancerAudit(false); setShowAuditRapide(true); }} className="px-4 py-2.5 rounded-xl bg-white/10 border border-white/20 text-white hover:bg-white/15 font-medium">
              {p('quick_audit_1min')}
            </button>
            <button type="button" onClick={handleRunFullAudit} disabled={auditRunning} className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-blue-500 to-cyan-600 text-white font-semibold shadow-lg flex items-center gap-2 disabled:opacity-50">
              {auditRunning ? <RefreshCw size={16} className="animate-spin" /> : <Shield size={16} />}
              {p('launch_full_audit')}
            </button>
          </>
        )}
      >
        <div className="space-y-4">
          <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
            <p className="text-blue-300 text-sm">{p('auto_audit_note')}</p>
          </div>
          <div className="space-y-4 pb-6 border-b border-white/10">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {[
                  { title: p('audit_score_compliance'), value: `${complianceData.overview.complianceScore}%`, glow: 'from-green-500 to-emerald-600' },
                  { title: p('audit_passed_checks'), value: complianceData.overview.passedChecks, glow: 'from-blue-500 to-cyan-600' },
                  { title: p('audit_warnings'), value: complianceData.overview.warningChecks, glow: 'from-orange-500 to-red-600' },
                ].map((stat, i) => (
                  <div key={i} className="relative group">
                    <div className={`absolute -inset-0.5 bg-gradient-to-r ${stat.glow} rounded-3xl opacity-0 group-hover:opacity-40 transition-opacity duration-500 blur-sm`} style={{ pointerEvents: 'none' }} />
                    <div className="relative bg-white/10 backdrop-blur-lg rounded-3xl p-5 border border-white/20">
                      <p className="text-white/60 text-sm">{stat.title}</p>
                      <p className="text-2xl font-bold text-white mt-1">{stat.value}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex flex-wrap gap-4 text-sm text-white/70">
                <span>{p('last_audit_label')}: <strong className="text-white">{complianceData.overview.lastAudit}</strong></span>
                <span>{p('failed_label')}: <strong className="text-red-400">{complianceData.overview.failedChecks}</strong></span>
              </div>
          </div>
          <div className="space-y-4 pb-6 border-b border-white/10">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2 md:col-span-1">
                <h4 className="block w-full text-white/70 text-sm font-medium text-right">{p('system_checks_title')}</h4>
                {[p('sys_check_file_integrity'), p('sys_check_digital_signatures'), p('sys_check_electronic_journal'), p('sys_check_software_versions'), p('sys_check_backups')].map((item, idx) => (
                  <label key={idx} className="flex items-center space-x-2 text-white text-sm bg-white/5 rounded-lg p-2">
                    <input type="checkbox" defaultChecked className="rounded accent-blue-500" />
                    <span>{item}</span>
                  </label>
                ))}
              </div>
              <div className="space-y-2 md:col-span-2">
                <h4 className="block w-full text-white/70 text-sm font-medium text-right">{p('data_controls_title')}</h4>
                {[p('data_check_entry_consistency'), p('data_check_debit_credit'), p('data_check_closed_exercises'), p('data_check_monthly_closures'), p('data_check_supporting_docs')].map((item, idx) => (
                  <label key={idx} className="flex items-center space-x-2 text-white text-sm bg-white/5 rounded-lg p-2">
                    <input type="checkbox" defaultChecked className="rounded accent-blue-500" />
                    <span>{item}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[p('audit_opt_detailed_report'), p('audit_opt_email_notification'), p('audit_opt_deep_mode')].map((item, idx) => (
                <label key={idx} className="flex items-center space-x-3 text-white text-sm bg-white/5 rounded-lg p-3">
                  <input type="checkbox" defaultChecked={idx < 2} className="rounded accent-cyan-500" />
                  <span>{item}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
      </ComplianceModalShell>

      {/* Sauvegarder Maintenant Modal */}
      {showSauvegarderMaintenant && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4" onClick={() => setShowSauvegarderMaintenant(false)} role="presentation">
          <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-2xl border border-white/20 p-6 max-w-3xl w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-white">{p('immediate_backup_title')}</h2>
              <button 
                onClick={() => setShowSauvegarderMaintenant(false)}
                className="text-white/60 hover:text-white transition-colors"
              >
                <X size={24} />
              </button>
            </div>
            
            <div className="space-y-6">
              <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-4">
                <p className="text-cyan-300 text-sm">{p('secure_backup_note')}</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-gradient-to-br from-blue-500/20 to-cyan-500/20 rounded-xl p-6">
                  <h4 className="text-white font-semibold mb-2">{p('estimated_size')}</h4>
                  <p className="text-3xl font-bold text-white">0 Mo</p>
                  <p className="text-white/60 text-sm mt-2">{p('data_and_documents')}</p>
                </div>

                <div className="bg-gradient-to-br from-green-500/20 to-emerald-500/20 rounded-xl p-6">
                  <h4 className="text-white font-semibold mb-2">{p('last_backup')}</h4>
                  <p className="text-lg font-bold text-white">{p('na')}</p>
                  <p className="text-white/60 text-sm mt-2">{p('never_performed')}</p>
                </div>

                <div className="bg-gradient-to-br from-purple-500/20 to-pink-500/20 rounded-xl p-6">
                  <h4 className="text-white font-semibold mb-2">{p('retention')}</h4>
                  <p className="text-3xl font-bold text-white">{p('retention_10y')}</p>
                  <p className="text-white/60 text-sm mt-2">{p('legal_duration')}</p>
                </div>
              </div>

              <div className="bg-white/5 rounded-xl p-6">
                <h3 className="text-white font-semibold mb-4">{p('data_to_backup')}</h3>
                <div className="space-y-3">
                  {[
                    p('backup_item_database'),
                    p('backup_item_journals'),
                    p('backup_item_supporting_docs'),
                    p('backup_item_config'),
                    p('backup_item_reports'),
                    p('backup_item_audit_trail'),
                  ].map((item, idx) => (
                    <label key={idx} className="flex items-center space-x-3 text-white">
                      <input type="checkbox" defaultChecked className="rounded" />
                      <span>{item}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="bg-white/5 rounded-xl p-6">
                <h3 className="text-white font-semibold mb-4">{p('backup_destination')}</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('main_location')}</label>
                    <select className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-2 text-white">
                      <option>{p('dest_local_disk')}</option>
                      <option>{p('dest_network')}</option>
                      <option>{p('dest_cloud')}</option>
                      <option>{p('dest_usb')}</option>
                    </select>
                  </div>
                  
                  <div className="space-y-2">
                    <label className="flex items-center space-x-3 text-white">
                      <input type="checkbox" defaultChecked className="rounded" />
                      <span>{p('opt_zip_compression')}</span>
                    </label>
                    <label className="flex items-center space-x-3 text-white">
                      <input type="checkbox" defaultChecked className="rounded" />
                      <span>{p('opt_checksum')}</span>
                    </label>
                    <label className="flex items-center space-x-3 text-white">
                      <input type="checkbox" className="rounded" />
                      <span>{p('opt_redundant_copy')}</span>
                    </label>
                  </div>
                </div>
              </div>

              <div className="flex gap-3">
                <button className="flex-1 bg-white/10 hover:bg-white/20 text-white py-3 rounded-lg font-semibold transition-all">
                  {p('schedule_later')}
                </button>
                <button className="flex-1 bg-gradient-to-r from-cyan-600 to-blue-600 text-white py-3 rounded-lg font-semibold hover:from-cyan-700 hover:to-blue-700 transition-all">
                  <Database size={20} className="inline mr-2" />
                  {p('start_backup')}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Audit Rapide Modal */}
      {showAuditRapide && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4" onClick={() => setShowAuditRapide(false)} role="presentation">
          <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-2xl border border-white/20 p-6 max-w-2xl w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-white">{p('quick_audit_title')}</h2>
              <button 
                onClick={() => setShowAuditRapide(false)}
                className="text-white/60 hover:text-white transition-colors"
              >
                <X size={24} />
              </button>
            </div>
            
            <div className="space-y-6">
              <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                <p className="text-green-300 text-sm">
                  {p('quick_audit_note')}
                </p>
              </div>

              <div className="bg-white/5 rounded-xl p-6">
                <h3 className="text-white font-semibold mb-4">{p('included_controls')}</h3>
                <div className="space-y-3">
                  {[
                    p('quick_check_main_files'),
                    p('quick_check_critical_signatures'),
                    p('quick_check_debit_credit'),
                    p('quick_check_fiscal_year'),
                    p('quick_check_active_journal'),
                  ].map((item, idx) => (
                    <div key={idx} className="flex items-center space-x-3 text-white">
                      <div className="w-2 h-2 rounded-full bg-green-500"></div>
                      <span>{item}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white/5 rounded-xl p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-white font-semibold">{p('estimated_duration')}</h4>
                    <p className="text-white/60 text-sm">{p('about_1_minute')}</p>
                  </div>
                  <div className="text-3xl font-bold text-blue-400">~60s</div>
                </div>
              </div>

              <button className="w-full bg-gradient-to-r from-green-600 to-emerald-600 text-white py-3 rounded-lg font-semibold hover:from-green-700 hover:to-emerald-700 transition-all">
                <Shield size={20} className="inline mr-2" />
                {p('launch_quick_audit')}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
      <JournalElectroniqueViewer
        isOpen={showJournalElectronique}
        onClose={() => setShowJournalElectronique(false)}
      />

      <VersionLogViewer
        isOpen={showVersionLog}
        onClose={() => setShowVersionLog(false)}
      />

      <ParameterComparisonTool
        isOpen={showParameterComparison}
        onClose={() => setShowParameterComparison(false)}
      />
    </>
  );
};

export default ConformiteAuditPage;
